import React, { memo, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { Button, Form, Modal, useForm } from '@bringg/react-components';

import { UserTypeFormInputs } from '../user-type-form-inputs/user-type-form-inputs';
import { useUserTypeModal } from '../user-type-modal-context';

import styles from './user-type-modal.module.scss';

const Title = memo(({ title }: { title: string }) => {
	return <span className={styles.modalHeader}>{title}</span>;
});

interface FooterProps {
	onClose: () => void;
	onModalOk: () => void;
	okButtonDisabled?: boolean;
	okButtonText: string;
	cancelButtonText: string;
	containErrors: boolean;
	errorMessage: string;
}

const Footer = memo(
	({
		onClose,
		onModalOk,
		okButtonDisabled,
		okButtonText,
		cancelButtonText,
		containErrors,
		errorMessage
	}: FooterProps) => {
		return (
			<div className={styles.footer}>
				<Button type="link" onClick={onClose} className={styles.cancelButton}>
					{cancelButtonText}
				</Button>
				<span className={styles.errorMessage}>{containErrors ? errorMessage : null}</span>
				<Button type="primary" disabled={okButtonDisabled} onClick={onModalOk}>
					{okButtonText}
				</Button>
			</div>
		);
	}
);

export const UserTypeModal = observer(() => {
	const { modalConfig } = useUserTypeModal();
	const [form] = useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [containErrors, setContainErrors] = useState(false);

	const { isOpen, translations, initialValues, onSubmit, onClose } = modalConfig;

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue(initialValues);
	}, [initialValues, form]);

	const submitForm = () => {
		setIsSaving(true);
		setContainErrors(false);

		form.validateFields()
			.then(async () => {
				const formData = form.getFieldsValue(true);

				await onSubmit(formData);
			})
			.catch(error => {
				console.error('Failed to save user type:', error);
				if (error?.errorFields?.length) {
					setContainErrors(true);
				}
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<Form form={form} layout="vertical">
			<Modal
				visible={isOpen}
				maskClosable={false}
				onCancel={onClose}
				width={660}
				className={styles.userTypeModal}
				title={<Title title={translations.modalTitle} />}
				footer={
					<Footer
						onModalOk={submitForm}
						onClose={onClose}
						okButtonDisabled={isSaving}
						okButtonText={translations.editButton}
						cancelButtonText={translations.cancel}
						errorMessage={translations.formErrorsMessage}
						containErrors={containErrors}
					/>
				}
			>
				<div className={styles.modalBody}>
					<UserTypeFormInputs />
				</div>
			</Modal>
		</Form>
	);
});
